import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ExternalLink, Gauge } from "lucide-react";
import {
  C, Panel, StatCard, PieCard, BarCard, LineCard, SimpleTable, Empty, StatusBadge,
} from "./reportsUi";

const Grid = ({ children, cols = "lg:grid-cols-4" }) => <div className={`grid grid-cols-2 ${cols} gap-3`}>{children}</div>;
const ChartRow = ({ children }) => <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{children}</div>;
const NotTracked = ({ children }) => (
  <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-[12.5px]" style={{ backgroundColor: "rgba(155,185,206,0.16)", color: C.navy }}>
    <span className="mt-0.5 inline-block h-2 w-2 rounded-full shrink-0" style={{ background: C.sky }} />
    <span>{children}</span>
  </div>
);

export const OverviewTab = ({ data }) => {
  const c = data.cards || {};
  const seo = data.seo;
  const navigate = useNavigate();
  return (
    <div className="space-y-4" data-testid="rc-overview">
      <Grid>
        <StatCard testid="ov-leads" label="Total leads" value={c.leads_total} tone={C.navy} hint={`${c.leads_today || 0} today`} />
        <StatCard testid="ov-consults" label="Consultations" value={c.consultations_requested} tone={C.sky} hint={`${c.consultations_completed || 0} completed`} />
        <StatCard testid="ov-overdue" label="Follow-ups overdue" value={c.follow_ups_overdue} tone={C.red} hint={`${c.follow_ups_due_today || 0} due today`} />
        <StatCard testid="ov-opps" label="Active opportunities" value={c.opportunities_active} tone={C.navy} />
        <StatCard testid="ov-pipeline" label="Pipeline value" value={c.pipeline_value} money tone={C.navy} />
        <StatCard testid="ov-received" label="Revenue received" value={c.revenue_received} money tone={C.green} />
        <StatCard testid="ov-pending" label="Revenue pending" value={c.revenue_pending} money tone={C.amber} />
        <StatCard testid="ov-partners" label="Partner referrals open" value={c.partner_referrals_open} tone={C.navy} />
      </Grid>
      <ChartRow>
        <LineCard title="Leads — last 14 days" data={data.trend} color={C.navy} testid="ov-trend" />
        <PieCard title="Leads by source" data={data.charts?.leads_by_source} testid="ov-src" />
      </ChartRow>
      <ChartRow>
        <BarCard title="Leads by service lane" data={data.charts?.leads_by_service_lane} testid="ov-lane" />
        <BarCard title="Open pipeline by stage" data={data.charts?.pipeline_by_stage} money color={C.green} testid="ov-stage" />
      </ChartRow>
      {seo && (
        <Panel title="SEO snapshot" testid="ov-seo"
          right={<Button size="sm" variant="outline" onClick={() => navigate("/seo-intelligence")} className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Gauge size={13} /> Open SEO</Button>}>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[13px]" style={{ color: C.charcoal }}>
            <span>Health <b style={{ color: C.green }}>{seo.score}/100 ({seo.label})</b></span>
            <span>High issues <b style={{ color: C.red }}>{seo.high_issues}</b></span>
            <span>Pages needing work <b>{seo.pages_needing_work}</b></span>
            {seo.top_action && <span>Top action: <b>{seo.top_action}</b></span>}
          </div>
        </Panel>
      )}
    </div>
  );
};

export const WebsiteTab = ({ data }) => (
  <div className="space-y-4" data-testid="rc-website">
    <NotTracked>{data.note}</NotTracked>
    <Grid cols="lg:grid-cols-3">
      <StatCard testid="web-page" label="Leads with page path" value={data.cards?.leads_with_page} />
      <StatCard testid="web-cta" label="Leads with CTA" value={data.cards?.leads_with_cta} />
      <StatCard testid="web-rev" label="Reverse-search leads" value={data.cards?.reverse_search_leads} tone={C.green} />
    </Grid>
    <ChartRow>
      <BarCard title="Leads by page path" data={data.charts?.by_page_path} testid="web-bypage" />
      <PieCard title="Leads by CTA / source" data={data.charts?.by_cta} testid="web-bycta" />
    </ChartRow>
    <BarCard title="Reverse-search topics" data={data.charts?.by_reverse_topic} testid="web-byrev" />
    <Panel title="Website analytics integrations" testid="web-integrations">
      <div className="grid gap-2 sm:grid-cols-3">
        {(data.integrations || []).map((i) => (
          <div key={i.name} className="rounded-lg border p-3 flex items-center justify-between" style={{ borderColor: C.line }}>
            <span className="text-[13px]" style={{ color: C.navy }}>{i.name}</span>
            <StatusBadge label={i.connected ? "Connected" : "Not connected yet"} tone={i.connected ? C.green : C.slate} />
          </div>
        ))}
      </div>
    </Panel>
  </div>
);

export const LeadsTab = ({ data }) => (
  <div className="space-y-4" data-testid="rc-leads">
    <Grid cols="lg:grid-cols-3"><StatCard testid="ld-total" label="Total leads" value={data.total} tone={C.navy} /></Grid>
    <ChartRow>
      <PieCard title="By source" data={data.charts?.by_source} testid="ld-src" />
      <BarCard title="By service lane" data={data.charts?.by_service_lane} testid="ld-lane" />
    </ChartRow>
    <ChartRow>
      <BarCard title="By territory" data={data.charts?.by_territory} testid="ld-terr" />
      <BarCard title="By pipeline stage" data={data.charts?.by_stage} testid="ld-stage" />
    </ChartRow>
    <ChartRow>
      <BarCard title="By assigned user" data={data.charts?.by_assigned} testid="ld-user" />
      <BarCard title="By page path" data={data.charts?.by_page_path} testid="ld-page" />
    </ChartRow>
    {(data.not_tracked?.medium || data.not_tracked?.campaign) && (
      <NotTracked>Marketing medium/campaign attribution is not tracked yet — add UTM capture to your forms to populate these breakdowns.</NotTracked>
    )}
  </div>
);

export const FormsChatTab = ({ data }) => (
  <div className="space-y-4" data-testid="rc-forms-chat">
    <Grid cols="lg:grid-cols-4">
      <StatCard testid="fc-forms" label="Form submissions" value={data.forms?.total} tone={C.navy} />
      <StatCard testid="fc-chats" label="Chat sessions" value={data.chat?.total} tone={C.sky} />
      <StatCard testid="fc-leads" label="Chat → lead created" value={data.chat?.lead_created} tone={C.green} />
      <StatCard testid="fc-handoff" label="Handoff requested" value={data.chat?.handoff_requested} tone={C.amber} />
    </Grid>
    <ChartRow>
      <PieCard title="Forms by type" data={data.forms?.by_type} testid="fc-formtype" />
      <PieCard title="Chats by status" data={data.chat?.by_status} testid="fc-chatstatus" />
    </ChartRow>
    <ChartRow>
      <BarCard title="Chats by service lane" data={data.chat?.by_service_lane} testid="fc-chatlane" />
      <BarCard title="Chats by territory" data={data.chat?.by_territory} testid="fc-chatterr" />
    </ChartRow>
    {!data.forms?.completed_vs_started_tracked && <NotTracked>Form “completed vs started” funnel isn’t tracked yet — only submitted forms are counted.</NotTracked>}
  </div>
);

export const ConsultationsTab = ({ data }) => {
  const c = data.cards || {};
  return (
    <div className="space-y-4" data-testid="rc-consultations">
      <Grid cols="lg:grid-cols-5">
        <StatCard testid="co-req" label="Requested" value={c.requested} tone={C.navy} />
        <StatCard testid="co-sched" label="Scheduled" value={c.scheduled} tone={C.sky} />
        <StatCard testid="co-done" label="Completed" value={c.completed} tone={C.green} />
        <StatCard testid="co-cancel" label="Cancelled" value={c.cancelled} tone={C.amber} />
        <StatCard testid="co-noshow" label="No-show" value={c.no_show} tone={C.red} />
      </Grid>
      <ChartRow>
        <PieCard title="By status" data={data.charts?.by_status} testid="co-status" />
        <BarCard title="By service lane" data={data.charts?.by_service_lane} testid="co-lane" />
      </ChartRow>
      <ChartRow>
        <BarCard title="By territory" data={data.charts?.by_territory} testid="co-terr" />
        <BarCard title="By assigned" data={data.charts?.by_assigned} testid="co-user" />
      </ChartRow>
    </div>
  );
};

export const ServiceLanesTab = ({ data }) => (
  <div className="space-y-4" data-testid="rc-service-lanes">
    <ChartRow>
      <BarCard title="Leads by lane" data={data.charts?.leads_by_lane} testid="sl-leads" />
      <BarCard title="Opportunities by lane" data={data.charts?.opportunities_by_lane} color={C.navy} testid="sl-opps" />
    </ChartRow>
    <ChartRow>
      <BarCard title="Consultations by lane" data={data.charts?.consultations_by_lane} testid="sl-consults" />
      <BarCard title="Revenue by lane" data={data.charts?.revenue_by_lane} money color={C.green} testid="sl-rev" />
    </ChartRow>
    <ChartRow>
      <BarCard title="Closed won by lane" data={data.charts?.closed_won_by_lane} color={C.green} testid="sl-won" />
      <BarCard title="Closed lost by lane" data={data.charts?.closed_lost_by_lane} color={C.red} testid="sl-lost" />
    </ChartRow>
  </div>
);

export const TerritoriesTab = ({ data }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-4" data-testid="rc-territories">
      <Panel title="Territory performance" subtitle="Aggregate view — open the deep dive for full territory reports"
        testid="terr-panel"
        right={<Button size="sm" variant="outline" onClick={() => navigate("/reports/territories")} className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><ExternalLink size={13} /> Territory Reports</Button>}>
        <SimpleTable testid="terr-table" money={["pipeline_value"]}
          columns={[{ key: "territory", label: "Territory" }, { key: "leads", label: "Leads" }, { key: "opportunities", label: "Opportunities" }, { key: "partner_referrals", label: "Partner referrals" }, { key: "pipeline_value", label: "Pipeline value" }]}
          rows={data.table || []} />
      </Panel>
      <BarCard title="Leads by territory" data={data.charts?.leads_by_territory} testid="terr-chart" />
    </div>
  );
};

export const PartnersTab = ({ data }) => {
  const c = data.cards || {};
  return (
    <div className="space-y-4" data-testid="rc-partners">
      <Grid cols="lg:grid-cols-4">
        <StatCard testid="pa-sent" label="Referrals sent" value={c.sent} tone={C.navy} />
        <StatCard testid="pa-accepted" label="Accepted" value={c.accepted} tone={C.green} />
        <StatCard testid="pa-awaiting" label="Awaiting response" value={c.awaiting_response} tone={C.amber} />
        <StatCard testid="pa-overdue" label="Overdue" value={c.overdue} tone={C.red} />
        <StatCard testid="pa-declined" label="Declined" value={c.declined} tone={C.slate} />
        <StatCard testid="pa-quotep" label="Quote pending" value={c.quote_pending} tone={C.amber} />
        <StatCard testid="pa-quoteu" label="Quote uploaded" value={c.quote_uploaded} tone={C.sky} />
        <StatCard testid="pa-completed" label="Completed" value={c.completed} tone={C.green} />
      </Grid>
      <ChartRow>
        <PieCard title="By status" data={data.charts?.by_status} testid="pa-status" />
        <BarCard title="By account type" data={data.charts?.by_account_type} testid="pa-type" />
      </ChartRow>
      <BarCard title="By service lane" data={data.charts?.by_service_lane} testid="pa-lane" />
    </div>
  );
};

export const FollowUpsTab = ({ data }) => {
  const c = data.cards || {};
  return (
    <div className="space-y-4" data-testid="rc-followups">
      <Grid cols="lg:grid-cols-5">
        <StatCard testid="fu-due" label="Due" value={c.due} tone={C.navy} />
        <StatCard testid="fu-today" label="Due today" value={c.due_today} tone={C.sky} />
        <StatCard testid="fu-overdue" label="Overdue" value={c.overdue} tone={C.red} />
        <StatCard testid="fu-done" label="Completed" value={c.completed} tone={C.green} />
        <StatCard testid="fu-rate" label="Completion rate" value={c.completion_rate == null ? null : `${c.completion_rate}%`} tone={C.green} />
      </Grid>
      <ChartRow>
        <BarCard title="By assignee" data={data.charts?.by_assignee} testid="fu-assignee" />
        <BarCard title="Overdue by assignee" data={data.charts?.overdue_by_assignee} color={C.red} testid="fu-overdue-chart" />
      </ChartRow>
      <PieCard title="By status" data={data.charts?.by_status} testid="fu-status" />
    </div>
  );
};

export const RevenueTab = ({ data }) => {
  const c = data.cards || {};
  return (
    <div className="space-y-4" data-testid="rc-revenue">
      <NotTracked>Internal, admin-only. Values come from stored opportunity data — no revenue is estimated or fabricated.</NotTracked>
      <Grid cols="lg:grid-cols-6">
        <StatCard testid="rv-projected" label="Projected" value={c.projected} money tone={C.navy} />
        <StatCard testid="rv-pending" label="Pending" value={c.pending} money tone={C.amber} />
        <StatCard testid="rv-received" label="Received" value={c.received} money tone={C.green} />
        <StatCard testid="rv-open" label="Open value" value={c.open_value} money tone={C.navy} />
        <StatCard testid="rv-won" label="Closed won" value={c.closed_won} tone={C.green} />
        <StatCard testid="rv-lost" label="Closed lost" value={c.closed_lost} tone={C.red} />
      </Grid>
      <ChartRow>
        <BarCard title="Value by stage" data={data.charts?.value_by_stage} money color={C.navy} testid="rv-vstage" />
        <BarCard title="Value by service lane" data={data.charts?.value_by_lane} money color={C.green} testid="rv-vlane" />
      </ChartRow>
      <ChartRow>
        <BarCard title="Value by territory" data={data.charts?.value_by_territory} money testid="rv-vterr" />
        <PieCard title="Count by revenue status" data={data.charts?.count_by_revenue_status} testid="rv-cstatus" />
      </ChartRow>
    </div>
  );
};

export const FranchiseTab = ({ data }) => (
  <div className="space-y-4" data-testid="rc-franchise">
    <Panel title="Franchise / branch performance" testid="fr-panel">
      <SimpleTable testid="fr-table" money={["pipeline_value"]}
        columns={[{ key: "franchise", label: "Franchise" }, { key: "status", label: "Status" }, { key: "leads", label: "Leads" }, { key: "consultations", label: "Consults" }, { key: "opportunities", label: "Opps" }, { key: "partner_referrals", label: "Referrals" }, { key: "pipeline_value", label: "Pipeline" }, { key: "follow_up_completion", label: "FU %" }]}
        rows={(data.table || []).map((r) => ({ ...r, follow_up_completion: r.follow_up_completion == null ? "—" : `${r.follow_up_completion}%` }))} />
    </Panel>
    <ChartRow>
      <BarCard title="Leads by franchise" data={data.charts?.leads_by_franchise} testid="fr-leads" />
      <BarCard title="Pipeline by franchise" data={data.charts?.pipeline_by_franchise} money color={C.green} testid="fr-pipe" />
    </ChartRow>
  </div>
);

export const SeoTab = ({ data }) => {
  const navigate = useNavigate();
  if (!data) return <Empty>SEO data unavailable.</Empty>;
  const col = data.label === "Not Enough Data" ? C.slate : data.score >= 85 ? C.green : data.score >= 60 ? C.amber : C.red;
  return (
    <div className="space-y-4" data-testid="rc-seo">
      <Panel title="SEO performance snapshot" testid="seo-panel"
        right={<Button size="sm" onClick={() => navigate("/seo-intelligence")} className="h-8 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white"><Gauge size={13} /> Open SEO Intelligence</Button>}>
        <div className="flex items-center gap-5">
          <div className="flex flex-col items-center rounded-[0.9rem] px-5 py-3" style={{ backgroundColor: `${col}12` }}>
            <span className="font-display font-bold text-4xl" style={{ color: col }}>{data.score}</span>
            <span className="text-[10px] uppercase" style={{ color: C.slate }}>/ 100</span>
          </div>
          <div className="text-[13.5px] space-y-1" style={{ color: C.charcoal }}>
            <div><StatusBadge label={data.label} tone={col} /> <span style={{ color: C.slate }}>· {data.scope_label}</span></div>
            <div>High-priority issues: <b style={{ color: C.red }}>{data.high_issues}</b></div>
            <div>Pages needing work: <b>{data.pages_needing_work}</b> · Pages audited: <b>{data.pages_total}</b></div>
            {data.top_action?.title && <div>Top action: <b>{data.top_action.title}</b></div>}
            {data.last_email && <div className="text-[12px]" style={{ color: C.slate }}>Last emailed {data.last_email.to} · {data.last_email.status}</div>}
          </div>
        </div>
      </Panel>
    </div>
  );
};
